import { useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { LlmModelOut, PurposeOut } from "@/api";
import { appState } from "@/app/appState";
import { ActionHandler } from "@/components/UI/tools";
import { ArrowDown, Close, Edit, Remove, Save } from "@/components/UI/svgs";
import { Card, EmptyRow, Field, Pill, Row, Section, SelectField } from "../ui";
import { fitsPurpose, isDecisionModel, isUsable, ModelsAdmin, useModelsAdmin } from "./useModelsAdmin";

export const MODELS_SECTIONS = ["providers", "catalog", "assignments"] as const;
export type ModelsSection = (typeof MODELS_SECTIONS)[number];

export const isModelsSection = (value: string | undefined): value is ModelsSection =>
    MODELS_SECTIONS.includes(value as ModelsSection);

export const modelsSectionTitle = (section: ModelsSection) =>
    appState.t(`settings.models.${section}`);

const SECTION_HINTS: Record<ModelsSection, string> = {
    providers: "settings.models.providersHint",
    catalog: "settings.models.catalogHint",
    assignments: "settings.models.assignmentsHint",
};

const compactNumber = (n?: number | null) => {
    if (!n) return "–";
    if (n >= 1_000_000) return `${+(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${Math.round(n / 1_000)}k`;
    return String(n);
};

const euro = (n?: number | null) => (n == null ? "–" : n.toFixed(2));

const modelName = (model?: LlmModelOut) => model?.display_name || model?.key || "";

// --------------------------------------------------------------------------
// Providers
// --------------------------------------------------------------------------

const ProvidersSection = observer(({ admin }: { admin: ModelsAdmin }) => (
    <Card>
        {admin.providers.length === 0 && <EmptyRow>{appState.t("common.loading")}</EmptyRow>}
        {admin.providers.map((provider) => {
            const test = admin.tests[provider.provider];
            return (
                <Row
                    key={provider.provider}
                    roomy
                    trailing={
                        <ActionHandler
                            title={appState.t("settings.models.test")}
                            funq={() => admin.testProvider(provider.provider)}
                            disabled={test === "running"}
                            className="btn-soft btn-sm shrink-0"
                        >
                            {test === "running" && <span className="loader w-1 h-1" />}
                            {appState.t("settings.models.test")}
                        </ActionHandler>
                    }
                >
                    <div className="flex flex-row flex-wrap items-center gap-[.5rem]">
                        <p className="l">{provider.label}</p>
                        <Pill tone={provider.configured ? "good" : "danger"}>
                            {provider.configured
                                ? appState.t("settings.models.configured")
                                : appState.t("settings.models.notConfigured")}
                        </Pill>
                        {provider.is_default && <Pill tone="accent">{appState.t("settings.models.default")}</Pill>}
                    </div>
                    <div className="flex flex-row flex-wrap gap-[.25rem] mt-[.35rem]">
                        {provider.env_vars.map((name) => (
                            <code key={name} className="code-chip code-chip-muted">{name}</code>
                        ))}
                    </div>
                    {test && test !== "running" && (
                        <p className={`${test.ok ? "detail-text-strong" : "error-text"} mt-[.35rem]`}>
                            {test.detail}
                            {test.latency_ms != null && ` · ${test.latency_ms} ms`}
                        </p>
                    )}
                </Row>
            );
        })}
    </Card>
));

// --------------------------------------------------------------------------
// Catalog
// --------------------------------------------------------------------------

const ModelNameCell = observer(({ admin, model }: { admin: ModelsAdmin; model: LlmModelOut }) => {
    const [editing, setEditing] = useState(false);
    const [name, setName] = useState(model.display_name);

    const save = async () => {
        await admin.updateModel(model.id, { display_name: name });
        setEditing(false);
    };
    const cancel = () => {
        setName(model.display_name);
        setEditing(false);
    };

    if (editing) {
        return (
            <div className="flex flex-row items-center min-w-[14rem]">
                <input
                    className="input input-mono"
                    value={name}
                    autoFocus
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") save();
                        if (e.key === "Escape") cancel();
                    }}
                />
                <ActionHandler title={appState.t("common.save")} funq={save} disabled={admin.saving} className="btn-ghost btn-sm">
                    <Save size={14} />
                </ActionHandler>
                <ActionHandler title={appState.t("common.cancel")} funq={cancel} className="btn-ghost btn-sm">
                    <Close size={14} />
                </ActionHandler>
            </div>
        );
    }

    return (
        <div className="flex flex-col min-w-0">
            <div className="flex flex-row items-center">
                <span className="truncate" title={model.description ?? undefined}>{modelName(model)}</span>
                <span className="reveal">
                    <ActionHandler
                        title={appState.t("settings.models.rename")}
                        funq={() => {
                            setName(model.display_name);
                            setEditing(true);
                        }}
                        className="btn-ghost btn-meta"
                    >
                        <Edit size={14} />
                    </ActionHandler>
                </span>
            </div>
            {model.display_name !== model.key && <span className="detail-text">{model.key}</span>}
        </div>
    );
});

const CatalogTable = observer(({ admin, models }: { admin: ModelsAdmin; models: LlmModelOut[] }) => (
    <div className="px-1 py-[.55rem] overflow-x-auto">
        <table className="data-table">
            <thead>
                <tr>
                    <th>{appState.t("settings.models.colEnabled")}</th>
                    <th>{appState.t("settings.models.model")}</th>
                    <th>{appState.t("settings.models.colContext")}</th>
                    <th>{appState.t("settings.models.colPrice")}</th>
                    <th>{appState.t("settings.models.colCapabilities")}</th>
                </tr>
            </thead>
            <tbody>
                {models.map((model) => (
                    <tr key={model.id} className={`reveal-host ${isUsable(model) ? "" : "data-table-row-inactive"}`}>
                        <td>
                            <button
                                role="switch"
                                aria-checked={model.enabled}
                                title={appState.t(model.enabled ? "settings.models.disable" : "settings.models.enable")}
                                className={`toggle ${model.enabled ? "toggle-on" : ""}`}
                                disabled={admin.saving || !model.available}
                                onClick={() => admin.updateModel(model.id, { enabled: !model.enabled })}
                            />
                        </td>
                        <td><ModelNameCell admin={admin} model={model} /></td>
                        <td className="whitespace-nowrap">
                            {compactNumber(model.context_size)} · {compactNumber(model.max_output_tokens)}
                        </td>
                        <td className="whitespace-nowrap">
                            {euro(model.pricing.input_token)} · {euro(model.pricing.output_token)}
                        </td>
                        <td>
                            <div className="flex flex-row flex-wrap gap-[.25rem]">
                                {!model.available && <Pill tone="warn">{appState.t("settings.models.unavailable")}</Pill>}
                                {model.input_modalities.filter((m) => m !== "text").map((m) => (
                                    <Pill key={m} tone="accent">{m}</Pill>
                                ))}
                                {model.supported_features.map((f) => (
                                    <Pill key={f}>{f}</Pill>
                                ))}
                            </div>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
));

const CatalogSection = observer(({ admin }: { admin: ModelsAdmin }) => {
    const [search, setSearch] = useState("");
    const [enabledOnly, setEnabledOnly] = useState(false);

    const visible = useMemo(() => {
        const needle = search.trim().toLowerCase();
        return admin.models.filter(
            (model) =>
                (!enabledOnly || model.enabled) &&
                (!needle ||
                    model.key.toLowerCase().includes(needle) ||
                    model.display_name.toLowerCase().includes(needle)),
        );
    }, [admin.models, search, enabledOnly]);

    return (
        <Card>
            <div className="row-form">
                <div className="flex flex-row items-center gap-1">
                    <input
                        className="search"
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={appState.t("common.filterPlaceholder")}
                    />
                    <button
                        type="button"
                        onClick={() => setEnabledOnly(!enabledOnly)}
                        className={`filter-toggle shrink-0 ${enabledOnly ? "filter-toggle-active" : ""}`}
                    >
                        {appState.t("settings.models.enabledOnly")}
                    </button>
                </div>
            </div>
            {visible.length === 0 ? (
                <EmptyRow>
                    {admin.loading ? appState.t("common.loading") : appState.t("settings.models.noModels")}
                </EmptyRow>
            ) : (
                <CatalogTable admin={admin} models={visible} />
            )}
        </Card>
    );
});

// --------------------------------------------------------------------------
// Task assignments
// --------------------------------------------------------------------------

const FallbackList = observer(({
    admin,
    purpose,
    primary,
    fallbackIds,
}: {
    admin: ModelsAdmin;
    purpose: PurposeOut;
    primary: LlmModelOut;
    fallbackIds: number[];
}) => {
    const byId = new Map(admin.models.map((model) => [model.id, model]));
    const save = (ids: number[]) => admin.assign(purpose.key, primary.id, ids);
    const moveUp = (index: number) => {
        const next = [...fallbackIds];
        [next[index - 1], next[index]] = [next[index], next[index - 1]];
        save(next);
    };
    const candidates = admin.models.filter(
        (model) =>
            isUsable(model) &&
            fitsPurpose(model, purpose) &&
            model.provider === primary.provider &&
            // LEGACY_TOOL_JUDGE: redundant with fitsPurpose once eval.judge is decision-only.
            isDecisionModel(model) === isDecisionModel(primary) &&
            model.id !== primary.id &&
            !fallbackIds.includes(model.id),
    );

    return (
        <div className="field">
            <small className="field-label">{appState.t("settings.models.fallbacks")}</small>
            <Card>
                {fallbackIds.length === 0 && <EmptyRow>{appState.t("settings.models.noFallbacks")}</EmptyRow>}
                {fallbackIds.map((id, index) => {
                    const model = byId.get(id);
                    return (
                        <Row
                            key={id}
                            leading={<span className="caption w-1 shrink-0">{index + 1}</span>}
                            trailing={
                                <div className="flex flex-row items-center shrink-0">
                                    {model && !isUsable(model) && (
                                        <Pill tone="warn" title={appState.t("settings.models.skippedTitle")}>
                                            {appState.t("settings.models.skipped")}
                                        </Pill>
                                    )}
                                    <span className="reveal flex flex-row">
                                        <ActionHandler
                                            title={appState.t("settings.models.moveUp")}
                                            funq={() => moveUp(index)}
                                            disabled={admin.saving || index === 0}
                                            className="btn-ghost btn-sm"
                                        >
                                            <span className="inline-flex rotate-180"><ArrowDown size={14} /></span>
                                        </ActionHandler>
                                        <ActionHandler
                                            title={appState.t("common.delete")}
                                            funq={() => save(fallbackIds.filter((other) => other !== id))}
                                            disabled={admin.saving}
                                            className="btn-ghost btn-sm"
                                        >
                                            <Remove size={14} />
                                        </ActionHandler>
                                    </span>
                                </div>
                            }
                        >
                            <p className="l2 truncate">{modelName(model) || `#${id}`}</p>
                        </Row>
                    );
                })}
                {candidates.length > 0 && (
                    <div className="row-form">
                        <SelectField
                            value={undefined}
                            disabled={admin.saving}
                            onChange={(id) => id !== undefined && save([...fallbackIds, id])}
                            options={[
                                { id: undefined, name: appState.t("settings.models.addFallback") },
                                ...candidates.map((model) => ({ id: model.id, name: modelName(model) })),
                            ]}
                        />
                    </div>
                )}
            </Card>
        </div>
    );
});

const AssignmentSection = observer(({ admin, purpose }: { admin: ModelsAdmin; purpose: PurposeOut }) => {
    const byId = new Map(admin.models.map((model) => [model.id, model]));
    const assigned = purpose.assignment ? byId.get(purpose.assignment.llm_model_id) : undefined;
    const parent = purpose.inherits
        ? admin.purposes.find((other) => other.key === purpose.inherits)
        : undefined;
    const inheritedModel = parent?.assignment ? byId.get(parent.assignment.llm_model_id) : undefined;

    const eligible = admin.models.filter((model) => isUsable(model) && fitsPurpose(model, purpose));
    const options = [
        ...(parent
            ? [{
                id: undefined,
                name: appState.t("settings.models.inherits", {
                    purpose: parent.label,
                    model: modelName(inheritedModel) || "–",
                }),
            }]
            : !assigned
                ? [{ id: undefined, name: appState.t("settings.models.pickModel") }]
                : []),
        ...(assigned && !eligible.includes(assigned)
            ? [{ id: assigned.id, name: `${modelName(assigned)} (${appState.t("settings.models.unusable")})` }]
            : []),
        ...eligible.map((model) => ({
            id: model.id,
            name: `${modelName(model)} · ${model.provider}`,
        })),
    ];

    const requirements = [
        ...purpose.required_modalities.filter((m) => m !== "text").map((m) => `${m} input`),
        ...purpose.required_features,
        // LEGACY_TOOL_JUDGE: becomes a plain "decision" requirement for eval.judge.
        ...(purpose.accepts_decision ? ["or decision"] : []),
    ];

    const status = !purpose.assignment
        ? parent
            ? <Pill>{appState.t("common.inherited")}</Pill>
            : <Pill tone="danger">{appState.t("settings.models.unassigned")}</Pill>
        : assigned && !isUsable(assigned)
            ? <Pill tone="warn">{appState.t("settings.models.unusable")}</Pill>
            : null;

    return (
        <Section
            title={purpose.label}
            action={
                <div className="flex flex-row flex-wrap items-center justify-end gap-[.25rem]">
                    {requirements.map((r) => (
                        <code key={r} className="code-chip code-chip-muted">{r}</code>
                    ))}
                    {status}
                </div>
            }
        >
            <div className="row-form">
                <p className="detail-text">{purpose.description}</p>
                <Field label={appState.t("settings.models.model")}>
                    <SelectField
                        value={assigned?.id}
                        disabled={admin.saving}
                        onChange={(id) => {
                            if (id === undefined && !parent) return;
                            const next = byId.get(id ?? -1);
                            const keep = (purpose.assignment?.fallback_llm_model_ids ?? []).filter((fid) => {
                                const fallback = byId.get(fid);
                                // LEGACY_TOOL_JUDGE: the isDecisionModel match is redundant once eval.judge is decision-only.
                                return fid !== id && !!fallback && !!next &&
                                    fallback.provider === next.provider &&
                                    isDecisionModel(fallback) === isDecisionModel(next);
                            });
                            admin.assign(purpose.key, id, keep);
                        }}
                        options={options}
                    />
                </Field>
                {assigned && (
                    <FallbackList
                        admin={admin}
                        purpose={purpose}
                        primary={assigned}
                        fallbackIds={purpose.assignment?.fallback_llm_model_ids ?? []}
                    />
                )}
            </div>
        </Section>
    );
});

const AssignmentsSection = observer(({ admin }: { admin: ModelsAdmin }) => (
    <div className="flex flex-col gap-2">
        {admin.purposes.length === 0 && (
            <Card><EmptyRow>{appState.t("common.loading")}</EmptyRow></Card>
        )}
        {admin.purposes.map((purpose) => (
            <AssignmentSection key={purpose.key} admin={admin} purpose={purpose} />
        ))}
    </div>
));

// --------------------------------------------------------------------------

/** Stays mounted while switching sections, so the loaded catalog is shared between them. */
export const ModelsInspector = observer(({ section }: { section: ModelsSection }) => {
    const admin = useModelsAdmin();
    const cortecs = admin.providers.find((p) => p.provider === "cortecs");
    const lastSync = admin.models
        .map((model) => model.synced_at)
        .filter(Boolean)
        .sort()
        .at(-1);

    return (
        <div className="flex flex-col gap-2 w-full">
            <div className="flex flex-col gap-1">
                <div className="flex flex-row flex-wrap items-start justify-between gap-1">
                    <div className="inspector-header">
                        <p className="eyebrow">{appState.t("settings.models")}</p>
                        <p className="h2 truncate">{modelsSectionTitle(section)}</p>
                    </div>
                    <div className="flex flex-row items-center">
                        <ActionHandler
                            title={appState.t("settings.kvp.reload")}
                            funq={() => admin.load()}
                            disabled={admin.loading || admin.saving}
                        >
                            {appState.t("settings.kvp.reload")}
                        </ActionHandler>
                        {section === "catalog" && (
                            <ActionHandler
                                title={appState.t("settings.models.sync")}
                                funq={() => admin.sync()}
                                disabled={admin.saving || !cortecs?.configured}
                                className="btn-primary"
                            >
                                {admin.saving && <span className="loader w-1 h-1" />}
                                {appState.t("settings.models.sync")}
                            </ActionHandler>
                        )}
                    </div>
                </div>
                <p className="help-text">{appState.t(SECTION_HINTS[section])}</p>
                {section === "catalog" && (
                    <p className="hint">
                        {lastSync
                            ? appState.t("settings.models.lastSync", { when: new Date(lastSync).toLocaleString() })
                            : appState.t("settings.models.neverSynced")}
                    </p>
                )}
            </div>
            {section === "providers" && <ProvidersSection admin={admin} />}
            {section === "catalog" && <CatalogSection admin={admin} />}
            {section === "assignments" && <AssignmentsSection admin={admin} />}
        </div>
    );
});

export const ModelsNav = observer(({
    selected,
    onSelect,
}: {
    selected?: ModelsSection;
    onSelect: (section: ModelsSection) => void;
}) => (
    <div className="flex flex-col gap-[.2rem] min-h-0">
        {MODELS_SECTIONS.map((section) => (
            <div
                key={section}
                onClick={() => onSelect(section)}
                className={`tree-node pl-[.5rem] ${selected === section ? "tree-node-selected" : ""}`}
            >
                <span className="tree-node-label">{modelsSectionTitle(section)}</span>
            </div>
        ))}
    </div>
));
